import ResponsiveImage, { BackgroundImage } from "../responsive-image/ResponsiveImage";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useResponsive } from "../../contexts/ResponsiveContext";
import "./Company_display.css";
import Image1 from "./images/image1.PNG";
import Image1Mobile from "./images/image1.PNG";
import portrait1 from "./images/reparatur.jpeg";
import portrait2 from "./images/kaufen_leihen.jpg";
import portrait3 from "./images/technik.jpeg";
import Shop from "../shop/Shop";
import InstrumentInvitation from "../shop/InstrumentInvitation";
import features from "../../config/features.json";

import enTranslations from "./en.json";
import deTranslations from "./de.json";

import { Slide } from "react-slideshow-image";
import "react-slideshow-image/dist/styles.css";

// Function to load slide data from public folder
const loadSlideData = async () => {
  try {
    const response = await fetch("/slideData.json");
    if (!response.ok) {
      throw new Error("Failed to load slide data");
    }
    return await response.json();
  } catch (error) {
    console.error("Error loading slide data:", error);
    return { slides: [], config: {} };
  }
};

// Load slide images from the component's image folder.
const importSlideImages = async (slides) => {
  const imageModules = {};

  for (const slide of slides) {
    try {
      const imageModule = await import(`./images/${slide.imageName}`);
      imageModules[slide.imageName] = imageModule.default;
    } catch (error) {
      console.warn(`Could not load image: ${slide.imageName}`, error);
    }
  }

  return imageModules;
};

const CompanyDisplay = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { contentMaxWidth } = useResponsive();
  const [slideData, setSlideData] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [slideDataJson, setSlideDataJson] = useState(null);

  // Helper function to get text in current language
  const getLocalizedText = (textObj, fallback = "") => {
    if (!textObj) return fallback;
    const currentLang = i18n.language || "de";
    return textObj[currentLang] || textObj["de"] || textObj["en"] || fallback;
  };

  // Load slide images on component mount
  useEffect(() => {
    const loadImages = async () => {
      const loadedSlideDataJson = await loadSlideData();
      const imageModules = await importSlideImages(loadedSlideDataJson.slides);

      const slidesWithImages = loadedSlideDataJson.slides.map((slide) => ({
        ...slide,
        image: imageModules[slide.imageName] || null,
      }));

      setSlideDataJson(loadedSlideDataJson);
      setSlideData(slidesWithImages);
      setImagesLoaded(true);
    };

    loadImages();
  }, []);

  // Function to handle navigation and scroll to top
  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  i18n.addResourceBundle("en", "companyDisplay", enTranslations, true, true);
  i18n.addResourceBundle("de", "companyDisplay", deTranslations, true, true);

  // Function to generate and download iCal file
  const downloadICalFile = (date, title, year) => {
    const currentLanguage = i18n.language;
    const prefix =
      currentLanguage === "de"
        ? "Geigenbau Jacobi - "
        : "Violin Making Jacobi - ";
    const eventTitle = prefix + title;

    const eventYear = year || new Date().getFullYear();
    const monthMap = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    };

    // Parse date or date range
    let startDate, endDate;

    if (date.includes(" - ")) {
      // Date range format like "12.Oct - 14.Oct"
      const [startDateStr, endDateStr] = date.split(" - ");
      const [startDay, startMonth] = startDateStr.split(".");
      const [endDay, endMonth] = endDateStr.split(".");

      startDate = `${eventYear}${monthMap[startMonth]}${startDay.padStart(
        2,
        "0",
      )}`;
      endDate = `${eventYear}${monthMap[endMonth]}${endDay.padStart(2, "0")}`;
    } else {
      // Single date format like "09.Nov"
      const [day, month] = date.split(".");
      startDate = `${eventYear}${monthMap[month]}${day.padStart(2, "0")}`;
      endDate = startDate;
    }

    // All events should be all-day events
    let eventStartTime, eventEndTime;

    // Start time is always the start date
    eventStartTime = startDate;

    // End time calculation
    if (startDate === endDate) {
      // Single day - end date should be the next day for all-day events
      const year = parseInt(startDate.substr(0, 4));
      const month = parseInt(startDate.substr(4, 2)) - 1; // 0-indexed
      const day = parseInt(startDate.substr(6, 2));

      const startDateObj = new Date(Date.UTC(year, month, day));
      startDateObj.setUTCDate(startDateObj.getUTCDate() + 1);
      eventEndTime = startDateObj.toISOString().substr(0, 10).replace(/-/g, "");
    } else {
      // Multi-day - end date should be the day after the last day
      const year = parseInt(endDate.substr(0, 4));
      const month = parseInt(endDate.substr(4, 2)) - 1; // 0-indexed
      const day = parseInt(endDate.substr(6, 2));

      const endDateObj = new Date(Date.UTC(year, month, day));
      endDateObj.setUTCDate(endDateObj.getUTCDate() + 1);
      eventEndTime = endDateObj.toISOString().substr(0, 10).replace(/-/g, "");
    }

    // Generate iCal content
    const icalContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Geigenbau Jacobi//Event//EN
BEGIN:VEVENT
UID:${Date.now()}@geigenbau-jacobi.com
DTSTAMP:${new Date().toISOString().replace(/[:-]/g, "").split(".")[0]}Z
DTSTART;VALUE=DATE:${eventStartTime}
DTEND;VALUE=DATE:${eventEndTime}
SUMMARY:${eventTitle}
DESCRIPTION:${eventTitle}
LOCATION:Geigenbau Jacobi, Geisenheim
END:VEVENT
END:VCALENDAR`;

    // Create and download file
    const blob = new Blob([icalContent], { type: "text/calendar" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${eventTitle.replace(/[^a-zA-Z0-9]/g, "_")}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Function to replace newlines with <br />
  const renderTextWithBreaks = (text) => {
    return text.split("\n").map((item, index) => (
      <React.Fragment key={index}>
        {item}
        <br />
      </React.Fragment>
    ));
  };

  return (
    <div
      className="container"
      style={{
        maxWidth: contentMaxWidth,
        margin: contentMaxWidth !== "100%" ? "0 auto" : "initial",
      }}
    >
      <div className="row">
        <div className="text-container">
          <p className="text">
            {renderTextWithBreaks(t("companyDisplay:welcome"))}
            <br />
            <br />
            {renderTextWithBreaks(t("companyDisplay:contact_info"))}
          </p>
        </div>
        <div className="image-container">
          <ResponsiveImage sizes="(max-width: 768px) 96vw, 64vw"
            src={Image1}
            alt={t("companyDisplay:welcome_image_alt")}
            className="image desktop-image"
          />
          <ResponsiveImage sizes="(max-width: 768px) 96vw, 64vw"
            src={Image1Mobile}
            alt={t("companyDisplay:welcome_image_mobile_alt")}
            className="image mobile-image"
          />
        </div>
      </div>

      {/* Portrait Tiles Section */}
      <div className="portrait-tiles-section">
        <div
          className="portrait-tile clickable-tile focus-portrait"
          onClick={() =>
            handleNavigation(
              i18n.language === "en" ? "/en/repair&restore" : "/repair&restore",
            )
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleNavigation(
                i18n.language === "en"
                  ? "/en/repair&restore"
                  : "/repair&restore",
              );
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={t("companyDisplay:repair_tile_aria_label")}
        >
          <ResponsiveImage sizes="(max-width: 768px) 96vw, 30vw"
            src={portrait1}
            alt={t("companyDisplay:repair_tile_alt")}
            className="portrait-tile-image"
          />
          <div className="portrait-tile-overlay">
            <h3 className="portrait-tile-title">
              {t("companyDisplay:tile1_title")}
            </h3>
          </div>
        </div>
        <div
          className="portrait-tile clickable-tile focus-portrait"
          onClick={() =>
            handleNavigation(
              i18n.language === "en" ? "/en/buy&rent" : "/buy&rent",
            )
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleNavigation(
                i18n.language === "en" ? "/en/buy&rent" : "/buy&rent",
              );
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={t("companyDisplay:buy_rent_tile_aria_label")}
        >
          <ResponsiveImage sizes="(max-width: 768px) 96vw, 30vw"
            src={portrait2}
            alt={t("companyDisplay:buy_rent_tile_alt")}
            className="portrait-tile-image"
          />
          <div className="portrait-tile-overlay">
            <h3 className="portrait-tile-title">
              {t("companyDisplay:tile2_title")}
            </h3>
          </div>
        </div>
        <div
          className="portrait-tile clickable-tile focus-portrait"
          onClick={() =>
            handleNavigation(
              i18n.language === "en"
                ? "/en/analyse&document"
                : "/analyse&document",
            )
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleNavigation(
                i18n.language === "en"
                  ? "/en/analyse&document"
                  : "/analyse&document",
              );
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={t("companyDisplay:analysis_tile_aria_label")}
        >
          <ResponsiveImage sizes="(max-width: 768px) 96vw, 30vw"
            src={portrait3}
            alt={t("companyDisplay:analysis_tile_alt")}
            className="portrait-tile-image"
          />
          <div className="portrait-tile-overlay">
            <h3 className="portrait-tile-title">
              {t("companyDisplay:tile3_title")}
            </h3>
          </div>
        </div>
      </div>

      <div className="slider-container">
        {!imagesLoaded ? (
          <div className="slider-loading">
            <p>Loading slides...</p>
          </div>
        ) : (
          <Slide easing="ease">
            {slideData.map((slide, index) => (
              <div className="slide" key={slide.id || index}>
                <div
                  className="slide-content"
                  role="img"
                  aria-label={getLocalizedText(slide.altText)}
                >
                  {slide.image && (
                    <BackgroundImage src={slide.image} sizes="96vw" coverHeight="80vh" />
                  )}
                  {/* Date overlay - top left */}
                  {slide.showDate && (
                    <div
                      className="slide-date-overlay focus-date"
                      onClick={() =>
                        downloadICalFile(
                          slide.date,
                          getLocalizedText(slide.title),
                          slide.year,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          downloadICalFile(
                            slide.date,
                            getLocalizedText(slide.title),
                            slide.year,
                          );
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={
                        slideDataJson?.ariaLabels?.slideDate
                          ? getLocalizedText(slideDataJson.ariaLabels.slideDate)
                              .replace("{title}", getLocalizedText(slide.title))
                              .replace("{date}", slide.date)
                              .replace("{year}", slide.year)
                          : `${getLocalizedText(slide.title)} ${slide.date} ${
                              slide.year
                            }`
                      }
                    >
                      <span className="date-text">{slide.date}</span>
                    </div>
                  )}

                  {/* Text overlay - bottom right */}
                  <div
                    className="slide-text-overlay focus-slide"
                    onClick={() => window.open(slide.link, "_blank")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        window.open(slide.link, "_blank");
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    style={{ "--hover-color": slide.hoverColor }}
                    aria-label={
                      (slideDataJson?.ariaLabels?.slideContent
                        ? getLocalizedText(
                            slideDataJson.ariaLabels.slideContent,
                          )
                            .replace("{title}", getLocalizedText(slide.title))
                            .replace(
                              "{description}",
                              getLocalizedText(slide.description),
                            )
                        : `${getLocalizedText(slide.title)} ${getLocalizedText(
                            slide.description,
                          )}`) +
                      (slide.boothLabel
                        ? ` ${getLocalizedText(slide.boothLabel)}`
                        : "")
                    }
                  >
                    <h3 className="slide-title">
                      {getLocalizedText(slide.title)}
                    </h3>
                    {slide.boothLabel && (
                      <p className="slide-booth-label">
                        {getLocalizedText(slide.boothLabel)}
                      </p>
                    )}
                    <p className="slide-description">
                      {getLocalizedText(slide.description)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </Slide>
        )}
      </div>

      {/* Shop Section */}
      {features.shopEnabled ? <Shop /> : <InstrumentInvitation />}
    </div>
  );
};

export default CompanyDisplay;
